import { makeAutoObservable, observable } from 'mobx';
import { ProjectModel, createObservableProject } from '../models/project';
import { DocData, Icon, RawProject, RawProjectFile } from '../interfaces/project';
import { FILES_LIST_TYPE, PROJECT_FILTER_TYPE, PROJECT_ICON_TYPE, TASK_LIST_TYPE } from '../../global/constants';
import { rootStore } from '../rootStore';
import { captureException } from '@sentry/react';
import { genericUpsert, genericRemove, GenericUpsertOptions, formatToIcon, mergeTaskFilter } from '../common';
import { NewTaskFilterInterface, RawProjectFilter } from '../../interfaces/TaskInterface';
import MobxProjectsCacheService from '../../services/mobxProjectsCacheService';
import { syncTasksForProjects } from '../../services/syncService';
import { ProjectTemplateModel } from '../models/projectTemplate';
import { isEmpty } from '../../helpers/common';
import { MessageDetailsInterface } from '../../interfaces/chatMessageInterface';

/**
 * Shape of a single project's preference document stored under
 * user_preferences_v2/{userId}/workspaces/{workspaceId}/projects/{projectId}.
 * Keyed by projectId when passed as a map.
 */
export type ProjectPreferenceData = {
  fav_index?: number;
  tasks_filter_type?: RawProjectFilter;
  list_items_display_filter?: { [key: number]: boolean };
};

export class CreateProjectInput {
  //Common
  id?: string;
  name: string = '';
  startDate: Date | null = new Date();
  dueDate: Date | null = null;
  createdBy?: string;

  //New variables
  projectUsers: string[] = [];
  icon?: Icon | null = null;

  // Others
  customerId: string | null = null;
  templateId: string | null = null;
  taskFilter: { taskViewType: number } = { taskViewType: TASK_LIST_TYPE.TASKS };

  selectedTemplate: ProjectTemplateModel | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  updateWith(project: ProjectModel) {
    this.id = project.id;
    this.customerId = project.customerId;
    this.createdBy = project.createdById;
    this.name = project.name;
    this.startDate = project.createdOn ? new Date(project.createdOn) : new Date();
    this.dueDate = project.dueDate ? new Date(project.dueDate) : null;
  }

  clear() {
    this.id = undefined;
    this.name = '';
    this.createdBy = undefined;
    this.startDate = null;
    this.dueDate = null;
    this.projectUsers = [];
    this.icon = undefined;
    this.templateId = null;
    this.customerId = null;
  }

  get users() {
    return rootStore.workspaceStore.currentWorkspace?.users?.filter((user) => this.projectUsers.includes(user?.id));
  }

  setName(name: string) {
    this.name = name;
    if (!this.icon && name) {
      this.icon = formatToIcon(name) || { type: PROJECT_ICON_TYPE.DEFAULT, color: 0 };
    }
  }

  setCustomerId(customerId: string) {
    this.customerId = customerId;
  }
  setStartDate(createdOn: Date | null) {
    this.startDate = createdOn;
  }

  hasProjectUser(userId: string) {
    return this.projectUsers.includes(userId);
  }

  addProjectUser(userId: string) {
    this.projectUsers.push(userId);
  }

  removeProjectUser(userId: string) {
    this.projectUsers = this.projectUsers.filter((id) => id !== userId);
  }

  clearProjectUser() {
    this.projectUsers = [];
  }

  setDueDate(dueDate: Date | null) {
    this.dueDate = dueDate;
  }

  setTemplateId(templateId: string | null) {
    this.templateId = templateId;
  }

  setSelectedTemplate(template: ProjectTemplateModel | null) {
    this.selectedTemplate = template;
  }

  setIcon(icon: Icon) {
    this.icon = icon;
  }

  setTaskFilter(viewType: number) {
    this.taskFilter = {
      taskViewType: viewType
    };
  }
}
export class ProjectStore {
  loading: boolean = true;
  // Indicates an ongoing sync operation (Firebase / IndexedDB)
  isSyncing: boolean = false;
  // Last sync error message, if any
  syncError: string | null = null;
  projects: ProjectModel[] = [];
  currentProject: ProjectModel | null = null;
  createProjectInput: CreateProjectInput = new CreateProjectInput();
  archiveProjects: ProjectModel[] = [];
  projectUserNotIn: ProjectModel[] = [];
  projectFiltersMap: Map<string, NewTaskFilterInterface> = new Map();
  sortType: number = PROJECT_FILTER_TYPE.CREATED_DATE;
  query: string = '';
  filesViewListType: number = FILES_LIST_TYPE.GRID;
  uploadFilePercent: number = 0;
  docData: DocData | null = null;
  fileViewData: RawProjectFile | null = null;
  discussionData: MessageDetailsInterface[] = [];
  // Project id of the messages page currently mounted. Lets the background message
  // sync know whether a project discussion view is open (and for which project) so it
  // can re-project freshly synced messages into discussionData. Null when none is open.
  activeDiscussionProjectId: string | null = null;
  // Server time of the last discussion sync; refresh fetches only messages changed after it.
  discussionUpdatedAt: string | null = null;
  // Cursor paging for the open discussion (older messages loaded on scroll-up).
  discussionCursor: { before: string; beforeId: string } | null = null;
  discussionHasMore: boolean = false;
  discussionLoadingOlder: boolean = false;
  // Bumped when loading an older page fails, so the list's scroll trigger re-arms for a retry.
  discussionLoadOlderFailures: number = 0;

  constructor() {
    makeAutoObservable(this, {
      // Use shallow observable for discussionData to prevent deep observation of nested objects
      // This significantly improves performance when dealing with many messages
      discussionData: observable.shallow
    });
  }

  get projectsById() {
    const map = new Map<string, ProjectModel>();
    this.projects.forEach((project) => map.set(project.id, project));
    return map;
  }

  get favoriteProjects() {
    return this.projects.filter((project) => project.isFavorite).toSorted((a, b) => a.index - b.index);
  }

  get filteredProjects() {
    const filteredProjects = this.projects.filter((project) =>
      project.name.toLowerCase().includes(this.query.toLowerCase())
    );

    if (this.sortType === PROJECT_FILTER_TYPE.CREATED_DATE) {
      return filteredProjects.toSorted((a, b) => b.createdOn.getTime() - a.createdOn.getTime());
    } else if (this.sortType === PROJECT_FILTER_TYPE.ALPHABETIC) {
      return filteredProjects.toSorted((a, b) => a.name.localeCompare(b.name));
    }

    return filteredProjects;
  }

  getProjectFilter(projectId: string) {
    return this.projectFiltersMap.get(projectId);
  }

  setFilesViewListType(filesViewListType: number) {
    this.filesViewListType = filesViewListType;
  }

  setUploadFilePercent(uploadFilePercent: number) {
    this.uploadFilePercent = uploadFilePercent;
  }

  setProjects(rawProjects: RawProject[]) {
    MobxProjectsCacheService.getInstance().setProjects(rawProjects);
    this.projects = rawProjects.map(createObservableProject);
    this.loading = false;
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  upsertProjects(
    rawProjects: RawProject[],
    { excludeFields, currentId }: GenericUpsertOptions<ProjectModel> = {
      excludeFields: [
        'index',
        'taskFilterType',
        'listItemsDisplayFilter',
        'taskGroups',
        'milestones',
        'archiveMilestoneList',
        'activityLogs'
      ],
      currentId: this.currentProject?.id
    }
  ) {
    const { newItems, updatedItems, updatedCurrentItem } = genericUpsert(
      this.projects,
      rawProjects,
      createObservableProject,
      {
        excludeFields,
        currentId
      }
    );

    if (updatedCurrentItem) {
      this.currentProject = updatedCurrentItem;
    }

    MobxProjectsCacheService.getInstance().setProjects(rawProjects);

    if (newItems.length > 0) {
      const newProjectIds = newItems.map((project) => project.id);
      syncTasksForProjects(newProjectIds, rootStore.workspaceStore.currentWorkspace?.id);
    }

    return { newItems, updatedItems };
  }

  syncList(newRawProjects: RawProject[], archivedProjects: ProjectModel[]) {
    if (archivedProjects.length > 0) {
      this.removeProjects(archivedProjects.map((project) => project.id));
    }

    if (newRawProjects.length > 0) {
      this.upsertProjects(newRawProjects);
    }
  }

  removeProjects(projectIds: string[]) {
    const projectTasksToRemove = rootStore.taskStore.tasks
      .filter((task) => projectIds.includes(task.projectId))
      .map((task) => task.id);

    if (projectTasksToRemove.length > 0) {
      rootStore.taskStore.removeTasks(projectTasksToRemove);
    }

    if (this.currentProject && projectIds.includes(this.currentProject.id)) {
      this.currentProject = null;
    }

    this.projects = genericRemove(this.projects, projectIds);

    MobxProjectsCacheService.getInstance().removeBulk(projectIds);
  }

  setQuery(query: string) {
    this.query = query;
  }

  setProjectUserNotIn(projectUserNotIn: RawProject[]) {
    this.projectUserNotIn = projectUserNotIn.map(createObservableProject);
  }

  setArchiveProjects(archiveProjects: RawProject[]) {
    this.archiveProjects = archiveProjects.map(createObservableProject);
  }

  removeArchiveProjects(projectIds: string[]) {
    this.archiveProjects = this.archiveProjects.filter((project) => !projectIds.includes(project.id));
  }

  setSortType(sortType: number) {
    this.sortType = sortType;
  }

  setCurrentProject(projectId: string) {
    if (isEmpty(projectId)) return;
    this.currentProject = this.projects.find((project) => project.id === projectId) || null;
  }

  /**
   * Applies project preference data (fav index, task filter, display properties) onto the
   * in-memory projects. Only the projects present in `changedProjectsData` are touched.
   *
   * This is fed directly by the snapshot diff from the projects-preferences listener
   * (see App.tsx) so that a single document change no longer triggers a full collection
   * read. It is also used for the one-time bulk apply after a project sync, where the
   * caller supplies the full preference map.
   *
   * @param changedProjectsData Map of projectId -> preference data for the changed documents.
   */
  updateWithFirebase(changedProjectsData: Record<string, ProjectPreferenceData>) {
    this.isSyncing = true;
    this.syncError = null;
    try {
      const workspaceId = rootStore.workspaceStore.currentWorkspace?.id;

      if (!workspaceId) {
        return;
      }

      if (!changedProjectsData || Object.keys(changedProjectsData).length === 0) {
        return;
      }
      const rawProjectFilters = getProjectFiltersMap(changedProjectsData);

      for (const project of this.projects) {
        // Only apply updates for projects whose preferences actually changed in this snapshot.
        const projectData = changedProjectsData[project.id];
        if (!projectData) continue;

        // Update Index
        const index = projectData.fav_index ?? new Date(project?.createdOn).getTime();
        project.index = index;
        const firebaseFilter = rawProjectFilters.get(project.id);
        const projectFilter = project.taskFilterType;
        // Update Task Filter Type
        if (firebaseFilter) {
          const updatedFilterType = mergeTaskFilter(firebaseFilter, projectFilter);
          project.taskFilterType = updatedFilterType;
        }
        // Update Display Properties
        const displayFilter = projectData.list_items_display_filter;
        if (displayFilter) {
          // Merge with existing defaults, don't overwrite completely
          project.listItemsDisplayFilter = { ...(project.listItemsDisplayFilter || {}), ...(displayFilter || {}) };
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Firebase sync failed';
      this.syncError = message;
      captureException(err);
      throw err;
    } finally {
      this.isSyncing = false;
    }
  }

  *updateWithIndexdb() {
    this.isSyncing = true;
    try {
      const rawProjects: RawProject[] = yield MobxProjectsCacheService.getInstance().getProjects();
      this.projects = rawProjects.map((rawProject) => createObservableProject(rawProject));
    } catch (err) {
      captureException(err);
      // Do not re-throw: IndexedDB failures are recoverable, allow caller to attempt Firebase sync
    } finally {
      this.isSyncing = false;
    }
  }

  joinProject(projectId: string) {
    const project = this.projectUserNotIn.find((project) => project.id === projectId);
    if (project) {
      this.projectUserNotIn = this.projectUserNotIn.filter((project) => project.id !== projectId);
    }
  }

  setFileViewData(data: RawProjectFile | null) {
    this.fileViewData = data;
  }

  setDocData(data: DocData) {
    this.docData = data;
  }

  updateDocData(propsName: string, value: string | boolean | undefined) {
    this.docData = {
      ...this.docData,
      [propsName]: value
    };
  }

  setDiscussionData(data: MessageDetailsInterface[]) {
    this.discussionData = data;
  }

  setActiveDiscussionProjectId(projectId: string | null) {
    if (this.activeDiscussionProjectId !== projectId) this.resetDiscussionSync();
    this.activeDiscussionProjectId = projectId;
  }

  // Updates the current discussion messages (not a render-time copy, which may be stale).
  updateDiscussionData(update: (messages: MessageDetailsInterface[]) => MessageDetailsInterface[]) {
    this.discussionData = update(this.discussionData);
  }

  setDiscussionMessages(
    projectId: string,
    messages: MessageDetailsInterface[],
    sync: { cursor: { before: string; beforeId: string } | null; hasMore: boolean; updatedAt: string | null }
  ) {
    if (this.activeDiscussionProjectId !== projectId) return;
    this.discussionData = messages;
    this.discussionCursor = sync.cursor;
    this.discussionHasMore = sync.hasMore;
    this.discussionUpdatedAt = sync.updatedAt;
  }

  setDiscussionLoadingOlder(loading: boolean) {
    this.discussionLoadingOlder = loading;
  }

  noteDiscussionLoadOlderFailure() {
    this.discussionLoadOlderFailures += 1;
  }

  resetDiscussionSync() {
    this.discussionUpdatedAt = null;
    this.discussionCursor = null;
    this.discussionHasMore = false;
    this.discussionLoadingOlder = false;
  }

  clearDiscussionData() {
    this.discussionData = [];
    this.activeDiscussionProjectId = null;
    this.resetDiscussionSync();
  }

  //clear project related data
  clearProjectData() {
    this.loading = true;
    this.projects = [];
    this.currentProject = null;
    this.createProjectInput = new CreateProjectInput();
    this.archiveProjects = [];
    this.projectUserNotIn = [];
    this.projectFiltersMap = new Map();
    this.filesViewListType = FILES_LIST_TYPE.GRID;
    this.uploadFilePercent = 0;
    this.docData = null;
    this.fileViewData = null;
    this.discussionData = [];
    this.activeDiscussionProjectId = null;
    this.resetDiscussionSync();
  }
}

const getProjectFiltersMap = (rawProjectFilters: Record<string, { tasks_filter_type?: RawProjectFilter }>) => {
  if (!rawProjectFilters) {
    return new Map();
  }

  const projectFiltersMap = new Map(
    Object.entries(rawProjectFilters).map(([key, { tasks_filter_type: value }]) => {
      const filter = {
        showSubTask: value?.showSubTask,
        showCompletedTaskByFilter: value?.showCompletedTask,
        showRecentlyCompletedTask: value?.showRecentlyCompletedTask,
        showEmptyGroup: value?.showEmptyGroup,
        taskViewType: value?.viewType,
        groupingStatus: value?.grouping,
        orderByStatus: value?.order
      };
      return [key, filter];
    })
  );

  return projectFiltersMap;
};
